/**
 * SVG Builder — pure function: state + computed geometry → SVG element.
 *
 * Phase 1-2: Static SVG shell + circular text.
 * Phase 4: Full state-driven rendering (outer ring, inner ring, center).
 * Phase 5-6: Foil gradients, monochrome filter, center artwork image.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

// ── Color resolution ─────────────────────────────────────

/** Return fill value: hex string for solid, gradient URL for metallic.
 *  Looks up the key in StickerConfig palette — no hardcoded foil checks. */
function resolveFill(key) {
  var pal = window.StickerConfig && window.StickerConfig.palette;
  if (!pal) return key || '#000';
  var entry = pal.ribbon[key] || pal.paper[key];
  if (!entry) return key || '#000';
  if (entry.type === 'metallic') return 'url(#' + entry.fillRef + ')';
  return entry.fillRef;
}

// ── Affine baking for the shared foil light plane (OPEN-12, 2026-08-08) ──
// The foil gradients are userSpaceOnUse over the 1200×1200 sticker viewBox
// (VC-010-D: ONE light plane across text, rings, dots and artwork). A
// per-element transform makes the element sample that gradient in ITS OWN
// local space — every transformed glyph then reads a tiny corner of the
// plane (all-bright text, dark dots). Rule: when a fill resolves to a
// gradient url, bake the whole transform chain into the geometry and emit
// the element WITHOUT transforms, so every element samples the same global
// plane. Non-gradient fills keep the old transform emission
// (byte-identical output).
function isGradientFill(fill) {
  return typeof fill === 'string' && fill.indexOf('url(') === 0;
}

function mulMatrix(a, b) {
  return {
    a: a.a * b.a + a.c * b.b,
    b: a.b * b.a + a.d * b.b,
    c: a.a * b.c + a.c * b.d,
    d: a.b * b.c + a.d * b.d,
    e: a.a * b.e + a.c * b.f + a.e,
    f: a.b * b.e + a.d * b.f + a.f,
  };
}

// ops: [{t:'translate',x,y},{t:'rotate',a},{t:'scale',x,y}] in the same
// order as the SVG transform list — matrix = op0·op1·…·opN (the rightmost
// op applies to the point first).
function bakeOpsMatrix(ops) {
  var m = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  ops.forEach(function (op) {
    if (op.t === 'translate') {
      m = mulMatrix(m, { a: 1, b: 0, c: 0, d: 1, e: op.x, f: op.y });
    } else if (op.t === 'rotate') {
      var r = op.a * Math.PI / 180;
      var c = Math.cos(r), s = Math.sin(r);
      m = mulMatrix(m, { a: c, b: s, c: -s, d: c, e: 0, f: 0 });
    } else if (op.t === 'scale') {
      m = mulMatrix(m, { a: op.x, b: 0, c: 0, d: op.y, e: 0, f: 0 });
    }
  });
  return m;
}

// Bake a fontkit path `d` (M/L/C/Q/Z + numbers) through the affine chain.
// Coordinates round to 4 decimals — sub-pixel on the 1200×1200 plane.
function bakeGlyphPath(d, ops) {
  var m = bakeOpsMatrix(ops);
  var toks = String(d).match(/[MLCQZmlcqz]|-?\d*\.?\d+(?:[eE][+-]?\d+)?/g);
  if (!toks) return d;
  var out = [];
  var cmd = null, numIdx = 1;
  for (var i = 0; i < toks.length; i++) {
    var t = toks[i];
    if (/[MLCQZmlcqz]/.test(t)) { cmd = t.toUpperCase(); out.push(t); numIdx = 1; continue; }
    if (cmd === 'Z') { out.push(t); continue; }
    if (numIdx % 2 === 1) {
      var x = parseFloat(t);
      var y = parseFloat(toks[i + 1]);
      out.push(Math.round((m.a * x + m.c * y + m.e) * 10000) / 10000);
      out.push(Math.round((m.b * x + m.d * y + m.f) * 10000) / 10000);
      i++;
    } else {
      out.push(t);
    }
    numIdx += 2;
  }
  return out.join(' ');
}

// ── SVG builders ──────────────────────────────────────────

function buildDefs(state, activeId) {
  const defs = document.createElementNS(SVG_NS, 'defs');

  // Sticker clip path
  const clip = document.createElementNS(SVG_NS, 'clipPath');
  clip.setAttribute('id', 'sticker-clip');
  const clipCircle = document.createElementNS(SVG_NS, 'circle');
  clipCircle.setAttribute('cx', CENTER_X);
  clipCircle.setAttribute('cy', CENTER_Y);
  clipCircle.setAttribute('r', 600);
  clip.appendChild(clipCircle);
  defs.appendChild(clip);

  // Safe margin clip (r = 600 - buffer)
  const buffer = state.style.printSafeBuffer || 100;
  const contentClip = document.createElementNS(SVG_NS, 'clipPath');
  contentClip.setAttribute('id', 'content-clip');
  const contentClipCircle = document.createElementNS(SVG_NS, 'circle');
  contentClipCircle.setAttribute('cx', CENTER_X);
  contentClipCircle.setAttribute('cy', CENTER_Y);
  contentClipCircle.setAttribute('r', 600 - buffer);
  contentClip.appendChild(contentClipCircle);
  defs.appendChild(contentClip);

  const shift = state.interactive ? state.interactive.foilShift || 0 : 0;
  var paperKey = state.style.paperColor;
  var preset = (window.StickerConfig && window.StickerConfig.palette
    && window.StickerConfig.palette.paper[paperKey]
    && window.StickerConfig.palette.paper[paperKey].foilPreset) || 'light';
  var isDark = (preset === 'dark');
  var goldStops = isDark
    ? ['#6B5124', '#B8933A', '#EBD385', '#B8933A', '#6B5124']
    : ['#453111', '#C69D38', '#FFFFFF', '#C69D38', '#30210A'];
  var silverStops = isDark
    ? ['#5A6066', '#B8BEC4', '#E2E6EA', '#B8BEC4', '#5A6066']
    : ['#333538', '#D1D5D8', '#FFFFFF', '#D1D5D8', '#212325'];
  defs.appendChild(buildFoilGradient('gold-foil', shift, ...goldStops));
  defs.appendChild(buildFoilGradient('silver-foil', shift, ...silverStops));

  // Alpha extraction filter for image mask — uses threshold based on active source
  var threshold = (activeId === 'upload' ? (state.sources.upload.threshold || 0.5) : (state.sources.design.centerThreshold || 0.5));
  defs.appendChild(buildExtractAlphaFilter(threshold));

  return defs;
}

function buildFoilGradient(id, shift, ...stops) {
  const grad = document.createElementNS(SVG_NS, 'linearGradient');
  grad.setAttribute('id', id);
  // VC-010-D (advisor): share ONE light plane across all elements —
  // userSpaceOnUse over the sticker viewBox, so the bright/dark boundary
  // is a single continuous diagonal through text, rings, dots and artwork
  // (default objectBoundingBox would re-derive per element).
  grad.setAttribute('gradientUnits', 'userSpaceOnUse');
  grad.setAttribute('x1', '0');
  grad.setAttribute('y1', '0');
  grad.setAttribute('x2', '1200');
  grad.setAttribute('y2', '1200');
  // Position distribution follows the stop count:
  //   3 stops — [0, p−4, p+4] rollback point (p = 60 + shift);
  //   5 stops — symmetric band, stop 0/4 fixed as environment anchors,
  //             stops 1/2/3 move together as a fixed-width block:
  //             [0, 40+shift, 50+shift, 60+shift, 100];
  //             ±12 travel keeps every corner bright + highlight + dark.
  let positions;
  if (stops.length === 3) {
    positions = [0, 56 + shift, 64 + shift];
  } else if (stops.length === 5) {
    positions = [0, 40 + shift, 50 + shift, 60 + shift, 100];
  } else {
    positions = [0, 25 + shift/2, 50 + shift, 75 + shift/2, 100];
  }
  stops.forEach((color, i) => {
    const stop = document.createElementNS(SVG_NS, 'stop');
    stop.setAttribute('id', `${id}-stop-${i}`);
    stop.setAttribute('offset', positions[i] + '%');
    stop.setAttribute('stop-color', color);
    // VC-010-D: no CSS transition on offset — smoothing is done by the
    // rAF inertia loop in app.js initDynamicFoil (CSS transition caused
    // chasing/jitter under continuous updates).
    grad.appendChild(stop);
  });
  return grad;
}

function buildExtractAlphaFilter(threshold) {
  const filter = document.createElementNS(SVG_NS, 'filter');
  filter.setAttribute('id', 'extract-alpha');

  // Convert image to pure white with alpha mask:
  // Dark pixels become opaque, Light pixels become transparent,
  // Existing transparent pixels stay transparent.
  // Formula: A' = A - 0.333*R - 0.333*G - 0.333*B
  const matrix = document.createElementNS(SVG_NS, 'feColorMatrix');
  matrix.setAttribute('type', 'matrix');
  matrix.setAttribute('values',
    `0 0 0 0 1
     0 0 0 0 1
     0 0 0 0 1
    -0.333 -0.333 -0.333 1 0`
  );
  matrix.setAttribute('in', 'SourceGraphic');
  matrix.setAttribute('result', 'lumMap');
  filter.appendChild(matrix);

  // 2. Linear threshold — dynamically adjustable slope/intercept
  // slope=1000 creates a near-instant cliff transition at the threshold value
  // threshold=0.5 → intercept=-500 → pixels darker than 50% go fully opaque
  const thresh = threshold || 0.5;
  const comp = document.createElementNS(SVG_NS, 'feComponentTransfer');
  comp.setAttribute('in', 'lumMap');
  comp.setAttribute('result', 'mask');
  const funcA = document.createElementNS(SVG_NS, 'feFuncA');
  funcA.setAttribute('type', 'linear');
  funcA.setAttribute('slope', '1000');
  funcA.setAttribute('intercept', String(-1000 * thresh));
  comp.appendChild(funcA);
  filter.appendChild(comp);

  return filter;
}

function buildPaper(color) {
  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('cx', CENTER_X);
  circle.setAttribute('cy', CENTER_Y);
  circle.setAttribute('r', 600);
  circle.setAttribute('fill', resolveFill(color));
  return circle;
}

function buildTrackCircle(r, offsetY = 0) {
  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('cx', CENTER_X);
  circle.setAttribute('cy', CENTER_Y + (offsetY || 0));
  circle.setAttribute('r', r);
  circle.setAttribute('fill', 'none');
  circle.setAttribute('stroke', '#333');
  circle.setAttribute('stroke-width', '1');
  circle.setAttribute('stroke-dasharray', '4 4');
  return circle;
}

/**
 * Layer 1 guard (WO §7, Advisor ruling Q2): classic call sites throw when
 * the font metrics module is absent — never continue with undefined.
 */
function fontMetricsModule() {
  const eng = window.StickerEngine || {};
  if (!eng.fontMetrics) {
    throw new Error('StickerEngine fontMetrics module not loaded (fontMetrics.js must load before rendering)');
  }
  return eng.fontMetrics;
}

/**
 * Layer 3 — ring text as glyph outlines (VC-011 Stage 2).
 * Places Layer 1 outline paths at Layer 2 positions/rotations. Emits
 * plain <path> — no <text>, no <textPath>, no dominant-baseline.
 *
 * Per-glyph transform (Stage 1 renderer rule, pinned spec §4):
 * the glyph's advance VERTICAL CENTRE line passes through the anchor
 * (local x offset −advance/2), then the whole glyph is scaled to the
 * font size (y-flipped for SVG y-down) — the same rule the Stage 1 diag
 * used.
 */
function buildRingGlyphText(sideLayout, font, fontSize, color) {
  const group = document.createElementNS(SVG_NS, 'g');
  if (!sideLayout || !sideLayout.chars || sideLayout.chars.length === 0) return group;
  const fm = fontMetricsModule();
  const scale = fontSize / font.unitsPerEm;
  const fill = resolveFill(color);
  // OPEN-12: gradient (metallic) fills share ONE global userSpaceOnUse
  // light plane — bake the per-glyph transform into the outline so the
  // glyph samples the 1200×1200 plane like the dots do, instead of a
  // per-glyph local sliver (the all-bright-text/dark-dots regression).
  const bake = isGradientFill(fill);
  sideLayout.chars.forEach((c) => {
    const d = fm.glyphPath(font, c.char);
    if (!d) return;
    const path = document.createElementNS(SVG_NS, 'path');
    if (bake) {
      path.setAttribute('d', bakeGlyphPath(d, [
        { t: 'translate', x: c.centerX, y: c.centerY },
        { t: 'rotate', a: c.tangentRotation },
        { t: 'translate', x: -c.advance / 2, y: 0 },
        { t: 'scale', x: scale, y: -scale },
      ]));
      path.setAttribute('fill', fill);
    } else {
      // Attribute ORDER is load-bearing for byte-identical non-gradient
      // output — keep d → fill → transform exactly as before.
      path.setAttribute('d', d);
      // Same fill resolution the old <text> ring used: palette key → hex or
      // gradient URL (metallic). A raw key here would break monochrome
      // production (black ≠ #000000) and foil rendering (gold-foil ≠ url()).
      path.setAttribute('fill', fill);
      path.setAttribute('transform',
        'translate(' + c.centerX + ', ' + c.centerY + ') rotate(' + c.tangentRotation +
        ') translate(' + (-c.advance / 2) + ', 0) scale(' + scale + ', ' + (-scale) + ')');
    }
    group.appendChild(path);
  });
  return group;
}

/**
 * Layer 3 — center text as glyph outlines (VC-011 Stage 3, S3-A).
 * Consumes the §4 layout (computed.centerLayout.centerText[slotKey]):
 * per-line startX (centred on CENTER_X) + baselineY (capHeight mid-split) +
 * per-char local x. Emits plain <path> per glyph — no <text>, no
 * <tspan>, no dominant-baseline, no font attributes (WO stage3 §5.1).
 * Fail-closed (Q4): the font must be registered — getFont throws.
 */
function buildCenterText(layout, font, color, layerOffsetY) {
  const fm = fontMetricsModule();
  const scale = layout.fontSize / font.unitsPerEm;
  const fill = resolveFill(color);
  // OPEN-12: same global light plane rule for gradient fills — bake the
  // layer/line/glyph transforms into each outline.
  const bake = isGradientFill(fill);
  const group = document.createElementNS(SVG_NS, 'g');
  layout.lines.forEach(function (line) {
    if (bake) {
      line.chars.forEach(function (c) {
        const d = fm.glyphPath(font, c.char);
        if (!d) return;
        const path = document.createElementNS(SVG_NS, 'path');
        path.setAttribute('d', bakeGlyphPath(d, [
          { t: 'translate', x: 0, y: layerOffsetY || 0 },
          { t: 'translate', x: line.startX, y: line.baselineY },
          { t: 'translate', x: c.x, y: 0 },
          { t: 'scale', x: scale, y: -scale },
        ]));
        path.setAttribute('fill', fill);
        group.appendChild(path);
      });
    } else {
      const gLine = document.createElementNS(SVG_NS, 'g');
      gLine.setAttribute('transform', 'translate(' + line.startX + ', ' + line.baselineY + ')');
      line.chars.forEach(function (c) {
        const d = fm.glyphPath(font, c.char);
        if (!d) return;
        const path = document.createElementNS(SVG_NS, 'path');
        path.setAttribute('d', d);
        // Same fill resolution the old <text> center used: palette key →
        // hex or gradient URL (metallic).
        path.setAttribute('fill', fill);
        path.setAttribute('transform',
          'translate(' + c.x + ', 0) scale(' + scale + ', ' + (-scale) + ')');
        gLine.appendChild(path);
      });
      group.appendChild(gLine);
    }
  });
  return group;
}

function buildCenterImage(dataUrl, bounds, ribbonColor, offsetY, rotation) {
  if (!dataUrl) return null;
  const size = Math.min(bounds.width, bounds.height);
  const oy = offsetY || 0;
  const rot = rotation || 0;

  const group = document.createElementNS(SVG_NS, 'g');
  const defs = document.createElementNS(SVG_NS, 'defs');

  const maskId = 'center-img-mask';
  const mask = document.createElementNS(SVG_NS, 'mask');
  mask.setAttribute('id', maskId);

  const image = document.createElementNS(SVG_NS, 'image');
  image.setAttribute('href', dataUrl);
  image.setAttribute('x', CENTER_X - size / 2);
  image.setAttribute('y', CENTER_Y - size / 2 + oy);
  image.setAttribute('width', size);
  image.setAttribute('height', size);
  image.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  image.setAttribute('filter', 'url(#extract-alpha)');

  mask.appendChild(image);
  defs.appendChild(mask);
  group.appendChild(defs);

  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('x', CENTER_X - size / 2);
  rect.setAttribute('y', CENTER_Y - size / 2 + oy);
  rect.setAttribute('width', size);
  rect.setAttribute('height', size);
  rect.setAttribute('fill', resolveFill(ribbonColor));
  rect.setAttribute('mask', `url(#${maskId})`);

  group.appendChild(rect);
  // VC-012-B: rotation around the image center (CX, CY + oy). Emitted ONLY
  // when rotation ≠ 0 so the default path stays byte-identical (规则 A).
  if (rot) {
    group.setAttribute('transform', 'rotate(' + rot + ', ' + CENTER_X + ', ' + (CENTER_Y + oy) + ')');
  }
  return group;
}

function buildFullBleedImage(dataUrl, ribbonColor, scale, fitMode) {
  if (!dataUrl) return null;
  // 图片按整个 2 英寸贴纸铺满（baseSize = 2×600，不随安全边距缩小）：
  // 客人上传的图一定是 2 英寸满贴；外圈仍由红线(content-clip)按生产安全裁切。
  var baseSize = 2 * 600;
  var size = baseSize * (scale || 1.0);

  var group = document.createElementNS(SVG_NS, 'g');
  var defs = document.createElementNS(SVG_NS, 'defs');

  var maskId = 'full-bleed-mask';
  var mask = document.createElementNS(SVG_NS, 'mask');
  mask.setAttribute('id', maskId);

  var image = document.createElementNS(SVG_NS, 'image');
  image.setAttribute('href', dataUrl);
  image.setAttribute('x', CENTER_X - size / 2);
  image.setAttribute('y', CENTER_Y - size / 2);
  image.setAttribute('width', size);
  image.setAttribute('height', size);
  image.setAttribute('preserveAspectRatio', 'xMidYMid ' + (fitMode || 'meet'));
  image.setAttribute('filter', 'url(#extract-alpha)');

  mask.appendChild(image);
  defs.appendChild(mask);
  group.appendChild(defs);

  var rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('x', CENTER_X - size / 2);
  rect.setAttribute('y', CENTER_Y - size / 2);
  rect.setAttribute('width', size);
  rect.setAttribute('height', size);
  rect.setAttribute('fill', resolveFill(ribbonColor));
  rect.setAttribute('mask', 'url(#' + maskId + ')');

  group.appendChild(rect);
  return group;
}

function buildRingDecorations(state, computed, ringKey) {
  const group = document.createElementNS(SVG_NS, 'g');

  // Decoration bypass: if show is false, render nothing
  if (state.decoration.show === false) return group;

  // Decoration bypass: if graphic is null/undefined, render nothing
  if (state.decoration.graphic === null || state.decoration.graphic === undefined) {
    return group;
  }

  const isOuter = ringKey === 'outerRing';
  const ringState = state[ringKey];
  if (!ringState || !ringState.enabled) return group;

  const decorations = computed[ringKey] && computed[ringKey].decorations;
  if (!decorations || decorations.length === 0) return group;

  const trackRadius = computed[isOuter ? 'outerTrackRadius' : 'innerTrackRadius'];
  const size = (state.decoration.baseSize || 60) * (trackRadius / 380);
  const fill = resolveFill(state.style.ribbonColor);
  const graphic = state.decoration.graphic;

  decorations.forEach((d, i) => {
    // VC-011 Stage 2: dots come from the Layer 2 layout output (absolute
    // coordinates, offsetY + rotation already applied) — the old
    // polarToCartesian(CENTER_X, CENTER_Y, trackRadius, d.angle) that ignored offsetY
    // is gone; dots can no longer disagree with the text.
    const pos = { x: d.x, y: d.y };

    if (typeof graphic === 'string' && (graphic.startsWith('M') || graphic.startsWith('m'))) {
      // VC-010-D (advisor): the current decoration is a radius-8 circle
      // path. Render it as a GLOBAL <circle> (no per-element translate/scale
      // transform) so it samples the userSpaceOnUse foil gradient on the
      // same 1200×1200 light plane as text/rings. Radius preserved: path
      // r=8 scaled by size/24 → size/3.
      if (graphic.replace(/\s+/g, ' ').trim() === 'M 8,0 A 8,8 0 1,0 -8,0 A 8,8 0 1,0 8,0 Z') {
        const circle = document.createElementNS(SVG_NS, 'circle');
        circle.setAttribute('cx', pos.x);
        circle.setAttribute('cy', pos.y);
        circle.setAttribute('r', size / 3);
        circle.setAttribute('fill', fill);
        group.appendChild(circle);
      } else {
        const path = document.createElementNS(SVG_NS, 'path');
        if (isGradientFill(fill)) {
          // OPEN-12: same global light plane rule — bake the decoration
          // transform into the outline.
          path.setAttribute('d', bakeGlyphPath(graphic, [
            { t: 'translate', x: pos.x, y: pos.y },
            { t: 'scale', x: size / 24, y: size / 24 },
          ]));
        } else {
          path.setAttribute('d', graphic);
          path.setAttribute('transform', `translate(${pos.x}, ${pos.y}) scale(${size / 24})`);
        }
        path.setAttribute('fill', fill);
        group.appendChild(path);
      }
    } else if (typeof graphic === 'string' && (graphic.startsWith('http') || graphic.startsWith('data:image'))) {
      // Custom image decoration — monochrome via alpha mask (like center artwork)
      const maskId = `${ringKey}-deco-mask-${i}`;
      const defs = document.createElementNS(SVG_NS, 'defs');
      const mask = document.createElementNS(SVG_NS, 'mask');
      mask.setAttribute('id', maskId);
      const img = document.createElementNS(SVG_NS, 'image');
      img.setAttribute('href', graphic);
      img.setAttribute('x', pos.x - size / 4);
      img.setAttribute('y', pos.y - size / 4);
      img.setAttribute('width', size / 2);
      img.setAttribute('height', size / 2);
      img.setAttribute('filter', 'url(#extract-alpha)');
      mask.appendChild(img);
      defs.appendChild(mask);
      group.appendChild(defs);

      const rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('x', pos.x - size / 4);
      rect.setAttribute('y', pos.y - size / 4);
      rect.setAttribute('width', size / 2);
      rect.setAttribute('height', size / 2);
      rect.setAttribute('fill', fill);
      rect.setAttribute('mask', `url(#${maskId})`);
      group.appendChild(rect);
    } else {
      // Fallback: default circle (preserves backward compat with "star" etc.)
      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', pos.x);
      circle.setAttribute('cy', pos.y);
      circle.setAttribute('r', size / 4);
      circle.setAttribute('fill', fill);
      group.appendChild(circle);
    }
  });

  return group;
}

/**
 * ASYMMETRIC Source Renderer — upload owns entire SVG, design appends to gRibbon.
 * Rationale: upload skips rings/tracks entirely (physical isolation).
 * Do NOT refactor to symmetric append pattern — would create dead nodes.
 */
const SourceRenderer = {
  upload: function(state, computed) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 1200 1200');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('xmlns', SVG_NS);

    // Defs with extract-alpha filter using upload threshold
    svg.appendChild(buildDefs(state, 'upload'));

    // Paper background (clipped to sticker edge)
    const gPaper = document.createElementNS(SVG_NS, 'g');
    gPaper.setAttribute('clip-path', 'url(#sticker-clip)');
    gPaper.appendChild(buildPaper(state.style.paperColor));
    svg.appendChild(gPaper);

    // Full-bleed masked image (uses extract-alpha + ribbonColor recolor)
    var fbDataUrl = state.sources.upload.imageDataUrl;
    var fbScale = state.sources.upload.scale || 1.0;
    const fbImg = buildFullBleedImage(fbDataUrl,
      state.style.ribbonColor, fbScale,
      state.sources.upload.fitMode || 'meet');
    if (fbImg) {
      const gFb = document.createElementNS(SVG_NS, 'g');
      // 红线(content-clip, r=600-安全边距)裁切保留 —— 生产安全，随滑块移动。
      gFb.setAttribute('clip-path', 'url(#content-clip)');
      gFb.appendChild(fbImg);
      svg.appendChild(gFb);
    }

    // Warning line for safe margin
    if (state.style.showTracks) {
      const buffer = state.style.printSafeBuffer || 100;
      const warning = document.createElementNS(SVG_NS, 'circle');
      warning.setAttribute('cx', CENTER_X);
      warning.setAttribute('cy', CENTER_Y);
      warning.setAttribute('r', 600 - buffer);
      warning.setAttribute('fill', 'none');
      warning.setAttribute('stroke', '#ff3b30');
      warning.setAttribute('stroke-width', '2');
      warning.setAttribute('stroke-dasharray', '8, 8');
      warning.setAttribute('opacity', '0.6');
      svg.appendChild(warning);
    }

    return svg;
  },

  design: function(gRibbon, source, state, computed) {
    // Graphic layer
    if (source.graphic && source.graphic.enabled !== false && source.graphic.visible && source.graphic.graphicId) {
      var gBounds = computed.centerLayout ? computed.centerLayout.graphicBounds : computed.centerBounds;
      const rib = state.style.ribbonColor || '#000';
      const goy = source.graphic.offsetY || 0;
      // OPEN-12: gradient fills bake the layer offset into the image/rect
      // geometry (shared global light plane); solid fills keep the group
      // transform (byte-identical non-gradient output).
      const img = buildCenterImage(source.graphic.graphicId, gBounds, rib,
        isGradientFill(resolveFill(rib)) ? goy : 0, source.graphic.rotation || 0);
      if (img) {
        const gGraphic = document.createElementNS(SVG_NS, 'g');
        if (goy && !isGradientFill(resolveFill(rib))) gGraphic.setAttribute('transform', 'translate(0, ' + goy + ')');
        gGraphic.appendChild(img);
        gRibbon.appendChild(gGraphic);
      }
    }

    // Text layers (text1, text2, text3) — Stage 3 (S3-A): consume the
    // §4 center-text layout; the renderer only places outlines.
    const rib = state.style.ribbonColor || '#000';
    ['text1', 'text2', 'text3'].forEach(function (slotKey) {
      const slot = source[slotKey];
      const layout = computed.centerLayout && computed.centerLayout.centerText && computed.centerLayout.centerText[slotKey];
      if (!(slot && slot.enabled !== false && slot.visible && slot.text && slot.text.trim().length > 0)) return;
      if (!layout) return;  // layoutSlot already filtered the same condition — defensive
      const fm = fontMetricsModule();
      const font = fm.getFont(slot.fontFamily, slot.fontWeight);
      const toy = slot.offsetY || 0;
      const ct = buildCenterText(layout, font, rib, toy);
      const gText = document.createElementNS(SVG_NS, 'g');
      // Stable identity marker for §5.3 non-center comparison (Advisor
      // 3-a, 2026-08-07): compare-noncenter.py strips center text BY
      // IDENTITY (this attribute), never by heuristic path inspection.
      gText.setAttribute('data-layer', 'center-text');
      // OPEN-12: gradient fills bake the layer offset into the outlines
      // (shared global light plane); solid fills keep the group transform.
      if (toy && !isGradientFill(resolveFill(rib))) gText.setAttribute('transform', 'translate(0, ' + toy + ')');
      gText.appendChild(ct);
      gRibbon.appendChild(gText);
    });
  }
};

/**
 * Build the complete sticker SVG.
 *
 * @param {object} state — full application state
 * @param {object} computed — computed geometry from layout engine
 * @returns {SVGSVGElement}
 */
function buildStickerSVG(state, computed) {
  var activeId = (state.ui && state.ui.previewOverride) || state.activeSourceId;

  // Upload: renderer owns entire SVG
  if (activeId === 'upload') {
    return SourceRenderer.upload(state, computed);
  }

  // Design: build standard shell, then renderer appends center layer
  const s = state;
  const rib = s.style.ribbonColor || '#000';

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 1200 1200');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('xmlns', SVG_NS);

  svg.appendChild(buildDefs(s, 'design'));

  // Paper is physically r=600, clipped to sticker edge
  const gPaper = document.createElementNS(SVG_NS, 'g');
  gPaper.setAttribute('clip-path', 'url(#sticker-clip)');
  gPaper.appendChild(buildPaper(s.style.paperColor));
  svg.appendChild(gPaper);

  // Content is strictly clipped to safe margin (r=600 - buffer)
  const gContent = document.createElementNS(SVG_NS, 'g');
  gContent.setAttribute('clip-path', 'url(#content-clip)');

  // Wrapper for ribbon elements to apply filter
  const gRibbon = document.createElementNS(SVG_NS, 'g');

  // Outer ring — Layer 2 output is absolute (rotation + offsetY already
  // applied inside the layout), so the old rotate() group transform is
  // gone; text and dots both consume the one layout output.
  if (s.outerRing.enabled) {
    const gOuterRing = document.createElementNS(SVG_NS, 'g');
    const ringComp = computed.outerRing;
    if (ringComp && ringComp.layout) {
      const fm = fontMetricsModule();
      const font = fm.getFont(s.outerRing.fontFamily, s.outerRing.fontWeight);
      if (ringComp.layout.top) {
        gOuterRing.appendChild(buildRingGlyphText(ringComp.layout.top, font, s.outerRing.topFontSize, rib));
      }
      if (ringComp.layout.bottom) {
        gOuterRing.appendChild(buildRingGlyphText(ringComp.layout.bottom, font, s.outerRing.bottomFontSize, rib));
      }
    }
    gOuterRing.appendChild(buildRingDecorations(s, computed, 'outerRing'));
    gRibbon.appendChild(gOuterRing);
  }

  // Inner ring
  if (s.innerRing.enabled) {
    const gInnerRing = document.createElementNS(SVG_NS, 'g');
    const ringComp = computed.innerRing;
    if (ringComp && ringComp.layout) {
      const fm = fontMetricsModule();
      const font = fm.getFont(s.innerRing.fontFamily, s.innerRing.fontWeight);
      if (ringComp.layout.top) {
        gInnerRing.appendChild(buildRingGlyphText(ringComp.layout.top, font, s.innerRing.topFontSize, rib));
      }
      if (ringComp.layout.bottom) {
        gInnerRing.appendChild(buildRingGlyphText(ringComp.layout.bottom, font, s.innerRing.bottomFontSize, rib));
      }
    }
    gInnerRing.appendChild(buildRingDecorations(s, computed, 'innerRing'));
    gRibbon.appendChild(gInnerRing);
  }

  // Center area via SourceRenderer.design (asymmetric: appends to gRibbon, no return value)
  SourceRenderer.design(gRibbon, s.sources.design, s, computed);

  gContent.appendChild(gRibbon);
  svg.appendChild(gContent);

  // Tracks overlay (drawn above content so they are always visible in editor)
  if (s.style.showTracks) {
    if (s.outerRing.enabled && computed.outerTrackRadius) svg.appendChild(buildTrackCircle(computed.outerTrackRadius, s.outerRing.offsetY || 0));
    if (s.innerRing.enabled && computed.innerTrackRadius) svg.appendChild(buildTrackCircle(computed.innerTrackRadius, s.innerRing.offsetY || 0));
    
    // Warning line for safe margin
    const buffer = s.style.printSafeBuffer || 100;
    const warning = document.createElementNS(SVG_NS, 'circle');
    warning.setAttribute('cx', CENTER_X);
    warning.setAttribute('cy', CENTER_Y);
    warning.setAttribute('r', 600 - buffer);
    warning.setAttribute('fill', 'none');
    warning.setAttribute('stroke', '#ff3b30'); // iOS Red
    warning.setAttribute('stroke-width', '2');
    warning.setAttribute('stroke-dasharray', '8, 8');
    warning.setAttribute('opacity', '0.6');
    svg.appendChild(warning);
  }

  return svg;
}
